import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, FileText, Loader2, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const INITIAL_PERSONA = {
  first_name: '', last_name: '', email: '', birth_date: '', gender: '', fiscal_code: '',
  birth_city: '', birth_province: '', birth_country: 'italia',
  residence_address: '', residence_street_number: '', residence_city: '',
  residence_postal_code: '', residence_province: '', residence_country: 'italia',
  document_type: '', document_number: '', release_date: '', expiration_date: '',
  release_city: '', release_province: '', public_authority: '',
  document_file: '', health_insurance_card_file: ''
};

const INITIAL_AZIENDA = {
  company_name: '', company_legal_form: '', company_tax_code: '', company_vat_number: '',
  company_certified_email: '', company_sdi: '',
  company_address: '', company_street_number: '', company_city: '',
  company_postal_code: '', company_province: '', company_country: 'italia',
  ...INITIAL_PERSONA,
  company_registration_report_file: ''
};

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const base64 = reader.result.split(',')[1];
    resolve(base64);
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const FileUploadField = ({ label, field, form, setForm, required = true }) => {
  const [fileName, setFileName] = useState('');
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') { toast.error('Solo file PDF'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('File troppo grande (max 10MB)'); return; }
    const base64 = await fileToBase64(file);
    setForm(f => ({ ...f, [field]: base64 }));
    setFileName(file.name);
  };
  return (
    <div>
      <Label className="text-xs">{label} {required && '*'}</Label>
      <div className="mt-1 relative">
        <label className={`flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer transition-colors ${
          form[field] ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-slate-300 bg-white hover:bg-slate-50 text-slate-600'
        }`} data-testid={`crif-file-${field}`}>
          {form[field] ? <FileText className="w-4 h-4 flex-shrink-0" /> : <Upload className="w-4 h-4 flex-shrink-0" />}
          <span className="text-sm truncate">{fileName || 'Carica PDF...'}</span>
          <input type="file" accept=".pdf" onChange={handleFile} className="hidden" />
        </label>
      </div>
    </div>
  );
};

const FormSection = ({ title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left" type="button">
        <span className="text-sm font-semibold text-slate-700">{title}</span>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {open && <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">{children}</div>}
    </div>
  );
};

const Field = ({ label, field, form, setForm, type = 'text', required = true, placeholder = '', maxLength, uppercase = false }) => (
  <div>
    <Label className="text-xs">{label} {required && '*'}</Label>
    <Input
      type={type}
      value={form[field] || ''}
      onChange={e => setForm(f => ({ ...f, [field]: uppercase ? e.target.value.toUpperCase() : e.target.value }))}
      placeholder={placeholder}
      maxLength={maxLength}
      className={`mt-1 text-sm ${uppercase ? 'uppercase' : ''}`}
      data-testid={`crif-${field}`}
    />
  </div>
);

export const CrifForm = ({ type = 'persona', onSubmit, loading = false }) => {
  const isAzienda = type === 'azienda';
  const [form, setForm] = useState(isAzienda ? { ...INITIAL_AZIENDA } : { ...INITIAL_PERSONA });

  const validate = () => {
    const required = isAzienda
      ? ['company_name', 'company_legal_form', 'company_tax_code', 'company_vat_number', 'company_certified_email',
         'company_address', 'company_street_number', 'company_city', 'company_postal_code', 'company_province', 'company_country',
         'first_name', 'last_name', 'birth_date', 'gender', 'fiscal_code',
         'birth_city', 'birth_province', 'birth_country',
         'residence_address', 'residence_street_number', 'residence_city',
         'residence_postal_code', 'residence_province', 'residence_country',
         'document_type', 'document_number', 'release_date', 'expiration_date',
         'release_city', 'release_province', 'public_authority',
         'document_file', 'health_insurance_card_file', 'company_registration_report_file']
      : ['first_name', 'last_name', 'email', 'birth_date', 'gender', 'fiscal_code',
         'birth_city', 'birth_province', 'birth_country',
         'residence_address', 'residence_street_number', 'residence_city',
         'residence_postal_code', 'residence_province', 'residence_country',
         'document_type', 'document_number', 'release_date', 'expiration_date',
         'release_city', 'release_province', 'public_authority',
         'document_file', 'health_insurance_card_file'];
    const missing = required.filter(f => !form[f]);
    if (missing.length > 0) {
      const labels = missing.slice(0, 3).map(f => f.replace(/_/g, ' ')).join(', ');
      toast.error(`Campi mancanti: ${labels}${missing.length > 3 ? ` e altri ${missing.length - 3}` : ''}`);
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onSubmit(form);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
        <p className="text-xs text-amber-700">
          La richiesta CRIF richiede documenti d'identità e delega. Tutti i campi contrassegnati con * sono obbligatori.
          I file devono essere in formato PDF (max 10MB).
        </p>
      </div>

      {isAzienda && (
        <FormSection title="Dati Azienda" defaultOpen={true}>
          <Field label="Ragione Sociale" field="company_name" form={form} setForm={setForm} placeholder="OpenAPI Srl" />
          <Field label="Forma Giuridica" field="company_legal_form" form={form} setForm={setForm} placeholder="srl" />
          <Field label="CF Azienda" field="company_tax_code" form={form} setForm={setForm} placeholder="12485671007" uppercase />
          <Field label="P.IVA Azienda" field="company_vat_number" form={form} setForm={setForm} placeholder="12485671007" />
          <Field label="PEC" field="company_certified_email" form={form} setForm={setForm} type="email" placeholder="azienda@pec.it" />
          <Field label="Codice SDI" field="company_sdi" form={form} setForm={setForm} required={false} placeholder="Opzionale" />
          <Field label="Indirizzo Sede" field="company_address" form={form} setForm={setForm} placeholder="Via Roma" />
          <Field label="N. Civico" field="company_street_number" form={form} setForm={setForm} placeholder="1" />
          <Field label="Città" field="company_city" form={form} setForm={setForm} placeholder="Roma" />
          <Field label="CAP" field="company_postal_code" form={form} setForm={setForm} placeholder="00100" maxLength={5} />
          <Field label="Provincia" field="company_province" form={form} setForm={setForm} placeholder="RM" maxLength={2} uppercase />
          <Field label="Paese" field="company_country" form={form} setForm={setForm} placeholder="italia" />
        </FormSection>
      )}

      <FormSection title={isAzienda ? "Rappresentante Legale - Dati Anagrafici" : "Dati Anagrafici"} defaultOpen={true}>
        <Field label="Nome" field="first_name" form={form} setForm={setForm} placeholder="Mario" />
        <Field label="Cognome" field="last_name" form={form} setForm={setForm} placeholder="Rossi" />
        {!isAzienda && <Field label="Email" field="email" form={form} setForm={setForm} type="email" placeholder="m.rossi@email.it" />}
        <Field label="Data di Nascita" field="birth_date" form={form} setForm={setForm} type="date" />
        <div>
          <Label className="text-xs">Sesso *</Label>
          <Select value={form.gender} onValueChange={v => setForm(f => ({ ...f, gender: v }))}>
            <SelectTrigger className="mt-1 text-sm" data-testid="crif-gender">
              <SelectValue placeholder="Seleziona..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="M">Maschio</SelectItem>
              <SelectItem value="F">Femmina</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Field label="Codice Fiscale" field="fiscal_code" form={form} setForm={setForm} placeholder="RSSMRA80A01H501U" maxLength={16} uppercase />
        <Field label="Città di Nascita" field="birth_city" form={form} setForm={setForm} placeholder="Roma" />
        <Field label="Provincia di Nascita" field="birth_province" form={form} setForm={setForm} placeholder="RM" maxLength={2} uppercase />
        <Field label="Paese di Nascita" field="birth_country" form={form} setForm={setForm} placeholder="italia" />
      </FormSection>

      <FormSection title={isAzienda ? "Rappresentante Legale - Residenza" : "Residenza"} defaultOpen={false}>
        <Field label="Indirizzo" field="residence_address" form={form} setForm={setForm} placeholder="Via Rossini" />
        <Field label="N. Civico" field="residence_street_number" form={form} setForm={setForm} placeholder="2" />
        <Field label="Città" field="residence_city" form={form} setForm={setForm} placeholder="Roma" />
        <Field label="CAP" field="residence_postal_code" form={form} setForm={setForm} placeholder="00100" maxLength={5} />
        <Field label="Provincia" field="residence_province" form={form} setForm={setForm} placeholder="RM" maxLength={2} uppercase />
        <Field label="Paese" field="residence_country" form={form} setForm={setForm} placeholder="italia" />
      </FormSection>

      <FormSection title={isAzienda ? "Rappresentante Legale - Documento" : "Documento d'Identità"} defaultOpen={false}>
        <div>
          <Label className="text-xs">Tipo Documento *</Label>
          <Select value={form.document_type} onValueChange={v => setForm(f => ({ ...f, document_type: v }))}>
            <SelectTrigger className="mt-1 text-sm" data-testid="crif-document_type">
              <SelectValue placeholder="Seleziona..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="IDENTITY_CARD">Carta d'Identità</SelectItem>
              <SelectItem value="DRIVING_LICENSE">Patente</SelectItem>
              <SelectItem value="PASSPORT">Passaporto</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Field label="Numero Documento" field="document_number" form={form} setForm={setForm} placeholder="IT567TRF" uppercase />
        <Field label="Data Rilascio" field="release_date" form={form} setForm={setForm} type="date" />
        <Field label="Data Scadenza" field="expiration_date" form={form} setForm={setForm} type="date" />
        <Field label="Città Rilascio" field="release_city" form={form} setForm={setForm} placeholder="Roma" />
        <Field label="Provincia Rilascio" field="release_province" form={form} setForm={setForm} placeholder="RM" maxLength={2} uppercase />
        <Field label="Ente Rilascio" field="public_authority" form={form} setForm={setForm} placeholder="Comune" />
      </FormSection>

      <FormSection title="Upload Documenti (PDF)" defaultOpen={true}>
        <FileUploadField label="Documento d'Identità" field="document_file" form={form} setForm={setForm} />
        <FileUploadField label="Tessera Sanitaria" field="health_insurance_card_file" form={form} setForm={setForm} />
        {isAzienda && <FileUploadField label="Visura Camerale" field="company_registration_report_file" form={form} setForm={setForm} />}
      </FormSection>

      <Button
        onClick={handleSubmit}
        disabled={loading}
        className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white"
        data-testid="crif-submit-btn"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
        Invia Richiesta CRIF {isAzienda ? 'Azienda' : 'Persona'}
      </Button>
    </div>
  );
};
